import { useCallback, useContext, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import clsx from 'clsx'
import { format as fnsFormat, isPast } from 'date-fns'

import { ArrowDownUpIcon, Text, UnstyledButton } from '@buffer-mono/popcorn'

import { CalendarContext } from '~publish/components/Calendar/context'
import { useCalendarMobileMode } from '~publish/components/Calendar/hooks/useCalendarMobileMode'
import { hasUnreadNotes } from '~publish/components/Notes'
import { usePostDetailsDialog } from '~publish/components/PostDialogs'
import { useCollaborationNotesPanel } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { format } from '~publish/helpers/temporal'
import { usePostUnreadNoteCount } from '~publish/hooks/notes/usePostNoteCounts'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'

import { CalendarPostPopover } from '../CalendarPostPopover'
import { PostMiniature } from '../PostMiniature'

import styles from './PostItem.module.css'

/**
 * This component renders an individual post item within the calendar.
 * It handles the display of post details and integrates with the PostMiniature.
 *
 * @component
 * @param {Object} props
 * @param {FragmentOf<typeof CalendarPostCard_Post>} props.post - The post data to display
 * @param {number} props.timestamp - The timestamp for the post
 * @param {'week' | 'month'} props.viewMode - The current calendar view mode
 * @param {boolean} props.viewOnly - Whether the post is in view-only mode
 * @param {string} props.timezone - The current timezone
 * @param {Function} props.setOpenModal - Function to control modal visibility
 *
 * @returns {JSX.Element | null} The rendered post item or null if no post data
 */
export function PostItem({
  post: maskedPost,
  timestamp,
  viewMode,
  timezone,
  isDraggingOver = false,
}: {
  post: FragmentOf<typeof CalendarPostCard_Post>
  timestamp: number
  viewMode: 'week' | 'month' | 'three-day'
  timezone: string
  isDraggingOver?: boolean
}): JSX.Element | null {
  const post = readFragment(CalendarPostCard_Post, maskedPost)
  const isMobile = useCalendarMobileMode()
  const isCompactColumn = useContext(CalendarContext)?.isCompact ?? false
  const isSmall = isMobile || isCompactColumn
  const { editPostInComposer } = usePostComposer()
  const { openDetailsDialog } = usePostDetailsDialog({
    postId: post?.id,
  })

  const [isOpen, setIsOpen] = useState(false)
  const { showPanel } = useCollaborationNotesPanel()
  const unreadNoteCount = usePostUnreadNoteCount(
    showPanel ? (post?.id ?? null) : null,
  )
  const markUnreadNotes = showPanel && hasUnreadNotes(unreadNoteCount)

  const ariaLabel = useMemo(() => {
    if (!post || !post.dueAt) return ''
    const day =
      Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
        timezone,
      )
    const scheduledDay = format(day, 'd MMMM EEEE yyyy')
    const channelService = post.channelService
    const formattedTime = fnsFormat(new Date(post.dueAt), 'HH:mm')
    const error = post.status === 'error' ? ' with an error' : ''
    const unreadNotes = markUnreadNotes ? ', unread Notes' : ''
    return `${channelService} post scheduled for ${scheduledDay}, ${formattedTime}${error}${unreadNotes}`
  }, [post, timestamp, timezone, markUnreadNotes])

  const handleDoubleClick = useCallback((): void => {
    if (!post) return

    setIsOpen(false)

    const isOverdue = post.dueAt && isPast(post.dueAt)
    const canEdit =
      ['draft', 'needs_approval'].includes(post.status) ||
      (post.status === 'scheduled' && !isOverdue)

    if (canEdit) {
      editPostInComposer({
        cta: 'publish-calendar-postItem-doubleClick-1',
        postId: post.id,
        contentItemId: post.postGroup?.id,
        contentItemTitle: post.postGroup?.title || null,
      })
    } else {
      openDetailsDialog({
        cta: 'publish-calendar-postItem-doubleClick-1',
        expandText: 'true',
      })
    }
  }, [post, editPostInComposer, openDetailsDialog])

  if (!post) {
    return null
  }

  return (
    <CalendarPostPopover
      post={maskedPost}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <UnstyledButton
        aria-label={ariaLabel}
        className={clsx(styles.postTriggerButton, {
          [styles.draggingOver]: isDraggingOver,
        })}
        onDoubleClick={handleDoubleClick}
      >
        <PostMiniature
          post={post}
          condensed={viewMode === 'month' || isSmall}
          compact={isSmall && viewMode === 'month'}
          className={styles.miniature}
          hasUnreadNotes={markUnreadNotes}
        />
        {isDraggingOver && (
          <div className={styles.draggingOverOverlay}>
            <ArrowDownUpIcon />
            <Text>Swap&nbsp;Posts</Text>
          </div>
        )}
      </UnstyledButton>
    </CalendarPostPopover>
  )
}
